import {
  ActionIcon,
  Badge,
  Flex,
  Loader,
  ScrollArea,
  Table,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { closeModal } from "@mantine/modals";
import { memo, useMemo } from "react";
import { IoIosArrowRoundBack } from "react-icons/io";
import {
  AccountReportInput,
  MemberStatus,
  NotificationType,
  useGetAccountDetailReportQuery,
} from "src/generated/graphql";
import { ModalId } from "src/utils/constants";

const cellStyle = { minWidth: "88px" };
const header = [
  "Location",
  "Account Status",
  "Account Type",
  "First Name",
  "Last Name",
  "DOB",
  "Gender",
  "Phone",
  "Email",
  "Address",
  "City",
  "State",
  "Zip Code",
  "Country",
  "License/ID",
  "Employer",
  "Primary",
  "Contact Preference",
];

type ITableReport = {
  values: AccountReportInput;
};

function TableReport({ values }: ITableReport) {
  const theme = useMantineTheme();
  let color = theme.colors.secondaryTextColor[6];
  const { data, loading } = useGetAccountDetailReportQuery({
    variables: { data: values },
  });

  const rows = useMemo(
    () =>
      data?.accountDetailReport.map((e) => (
        <tr key={e.id}>
          <td style={cellStyle}>
            {e.locations?.map((loc) => (
              <Badge key={loc.id} mr={4}>
                {loc.name}
              </Badge>
            ))}
            {!e.locations && "N/A"}
          </td>
          <td style={cellStyle}>{e.standing}</td>
          <td style={cellStyle}>
            {e.memberStatus === MemberStatus.Lead
              ? `${e.memberStatus} ${e.leadStatus ? `- ${e.leadStatus}` : ""}`
              : e.memberStatus}
          </td>
          <td style={cellStyle}>{e.user.firstName}</td>
          <td style={cellStyle}>{e.user.lastName}</td>
          <td style={cellStyle}>{e.dob || "N/A"}</td>
          <td style={cellStyle}>{e.gender || "N/A"}</td>
          <td style={cellStyle}>{e.user.phoneNumber || "N/A"}</td>
          <td style={cellStyle}>{e.user.email}</td>
          <td style={cellStyle}>{e.address || "N/A"}</td>
          <td style={cellStyle}>{e.city || "N/A"}</td>
          <td style={cellStyle}>{e.state || "N/A"}</td>
          <td style={cellStyle}>{e.zipCode || "N/A"}</td>
          <td style={cellStyle}>{e.country || "N/A"}</td>
          <td style={cellStyle}>{e.idNumber || "N/A"}</td>
          <td style={cellStyle}>{e.employer || "N/A"}</td>
          <td style={cellStyle}>
            {e.agreements?.find((sa) => sa.subscription.isActive)
              ? "True"
              : "False"}
          </td>
          <td style={cellStyle}>
            {e.preferences?.notificationType || NotificationType.None}
          </td>
        </tr>
      )),
    [data?.accountDetailReport]
  );

  const handleBack = () => {
    closeModal(ModalId.ReportView);
  };

  const headerMemo = useMemo(() => {
    return header.map((e) => <th>{e}</th>);
  }, []);

  return (
    <Flex direction={"column"} px={"sm"}>
      <ActionIcon
        mt={"md"}
        onClick={handleBack}
        size={"lg"}
        sx={{
          "&:hover": {
            backgroundColor: theme.colors.secondary[6],
          },
        }}
      >
        <IoIosArrowRoundBack color={color} size={32} />
      </ActionIcon>

      {!rows?.length ? (
        <Flex justify={"center"} align={"center"} mt={"lg"}>
          {loading ? (
            <Loader />
          ) : (
            <Text fw={"bold"} size={"xl"}>
              No Record Found
            </Text>
          )}
        </Flex>
      ) : (
        <ScrollArea
          style={{ height: "75vh" }}
          type="auto"
          px={"sm"}
          offsetScrollbars
        >
          <Table>
            <thead>
              <tr>{headerMemo}</tr>
            </thead>

            <tbody>{rows}</tbody>
          </Table>
        </ScrollArea>
      )}
    </Flex>
  );
}

export default memo(TableReport);
